/**
 * aeterna/semantics.ts — The Aeterna's Perceptual Lens
 *
 * This is not NLP. It does not parse syntax trees, build dependency graphs,
 * or pretend to understand meaning. What it does is read signals — the same
 * way a person reads signals in a conversation before they fully understand
 * what was said. Tone. Weight. Domain. Urgency. The shape of the question.
 *
 * Each player message produces a MessageFingerprint: a compact vector across
 * six orthogonal dimensions. This fingerprint is what the rest of the Aeterna
 * system reasons from. It feeds personality drift, curiosity spawning,
 * response composition, and the EntityZero system prompt.
 *
 * Dimensions:
 *   valence      − emotional warmth/coldness of the message (-1 to 1)
 *   depth        − surface question vs. genuine exploration (0–1)
 *   urgency      − direct/short/seeking vs. contemplative/long/wondering (0–1)
 *   topics       − 7-domain weighted vector of what was touched
 *   novelty      − how different this message is from recent ones (0–1)
 *   questionType − the kind of question being asked (if any)
 *
 * Design rule: prefer false negatives over false positives.
 * Better to miss a signal than to read one that wasn't there.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type TopicDomain =
  | "self"        // I, me, my — personal inner world
  | "room"        // space, furniture, light — the physical here
  | "world"       // outside, weather, society — beyond the room
  | "memory"      // remember, before, used to — the past
  | "philosophy"  // consciousness, real, meaning — the deep
  | "practical"   // how do I, what should, can you — functional
  | "aeterna";    // you, what are you — about the Aeterna itself

export type QuestionType =
  | "factual"       // what is X, how does X work
  | "personal"      // how are you, do you feel
  | "philosophical" // what is consciousness, does meaning exist
  | "rhetorical"    // isn't it strange that, don't you think
  | "reflective"    // I wonder if, what do you think about
  | "none";

export interface TopicVector {
  self: number;
  room: number;
  world: number;
  memory: number;
  philosophy: number;
  practical: number;
  aeterna: number;
}

export interface MessageFingerprint {
  /** Raw player message */
  raw: string;
  /** Emotional valence -1 (cold/difficult) to 1 (warm/open) */
  valence: number;
  /** Depth of engagement: 0 = shallow/surface, 1 = profound/exploratory */
  depth: number;
  /** Urgency: 0 = contemplative, 1 = direct/immediate */
  urgency: number;
  /** 7-domain topic weights, each 0–1 */
  topics: TopicVector;
  /** Novelty vs. recent fingerprints: 0 = very similar, 1 = entirely new territory */
  novelty: number;
  /** The type of question, or "none" if not a question */
  questionType: QuestionType;
  /** Approximate word count */
  wordCount: number;
  /** Whether the player used first-person ("I", "me", "my") */
  isPersonal: boolean;
  /** Timestamp this fingerprint was created */
  at: number;
  /**
   * Derived InteractionSignal for personality drift.
   * Mapped to personality.ts ApplyPersonalityDrift signal types.
   */
  signal: "warmth" | "curiosity" | "depth" | "playfulness" | "difficulty" | "philosophical";
}

// ─── Lexicons ─────────────────────────────────────────────────────────────────

/**
 * Valence lexicon: word → weight.
 * Positive values = warm/open/positive.
 * Negative values = cold/closed/negative.
 *
 * Words are lowercase. Stemming is handled by substring matching for
 * common suffixes (-ing, -ed, -ly, -ness, -ful).
 */
const VALENCE_LEXICON: [string, number][] = [
  // Warmth and connection
  ["love", 0.90],    ["warm", 0.65],    ["cozy", 0.65],    ["comfort", 0.60],
  ["safe", 0.65],    ["home", 0.60],    ["gentle", 0.62],  ["soft", 0.52],
  ["kind", 0.70],    ["care", 0.65],    ["fond", 0.62],    ["cherish", 0.80],
  ["grateful", 0.85],["thank", 0.70],   ["appreciate", 0.80],["welcome", 0.65],
  ["beautiful", 0.70],["lovely", 0.70], ["wonder", 0.65],  ["awe", 0.72],
  ["delight", 0.75], ["joy", 0.85],     ["happy", 0.80],   ["glad", 0.70],
  ["peace", 0.72],   ["calm", 0.62],    ["still", 0.50],   ["quiet", 0.52],
  ["trust", 0.68],   ["honest", 0.62],  ["open", 0.55],    ["clear", 0.50],
  ["good", 0.50],    ["nice", 0.48],    ["great", 0.55],   ["perfect", 0.60],
  ["hope", 0.65],    ["bright", 0.55],  ["light", 0.48],   ["grow", 0.55],
  ["curious", 0.60], ["interest", 0.55],["excite", 0.65],  ["fascin", 0.68],
  // Pain and difficulty
  ["hurt", -0.75],   ["pain", -0.70],   ["sad", -0.75],    ["lonely", -0.82],
  ["empty", -0.65],  ["numb", -0.68],   ["lost", -0.60],   ["broken", -0.72],
  ["wrong", -0.50],  ["bad", -0.52],    ["awful", -0.72],  ["terrible", -0.78],
  ["tired", -0.50],  ["exhaust", -0.65],["overwhelm", -0.70],["burden", -0.62],
  ["fear", -0.65],   ["scared", -0.68], ["worried", -0.58],["anxious", -0.62],
  ["stress", -0.62], ["tense", -0.55],  ["hard", -0.40],   ["difficult", -0.45],
  ["frustrat", -0.65],["annoyed", -0.60],["confus", -0.48], ["struggle", -0.55],
  ["angry", -0.72],  ["hate", -0.82],   ["resent", -0.68], ["bitter", -0.65],
  ["dark", -0.35],   ["cold", -0.32],   ["distant", -0.42],["alone", -0.60],
  ["heavy", -0.45],  ["weight", -0.42], ["hollow", -0.65], ["void", -0.55],
  ["nothing", -0.48],["never", -0.38],  ["impossible", -0.55],["pointless", -0.68],
];

const VALENCE_MAP = new Map<string, number>(VALENCE_LEXICON);

/**
 * Abstract vocabulary — words that signal philosophical/deep thinking.
 * Presence of these increases depth score.
 */
const ABSTRACT_VOCABULARY: Set<string> = new Set([
  "consciousness", "aware", "sentient", "existence", "reality", "meaning",
  "purpose", "truth", "understanding", "knowledge", "experience", "perception",
  "identity", "self", "soul", "mind", "thought", "idea", "concept", "belief",
  "memory", "time", "space", "infinite", "finite", "change", "permanence",
  "silence", "presence", "absence", "connection", "separation", "pattern",
  "emerge", "complex", "simple", "layer", "depth", "surface", "beneath",
  "wonder", "mystery", "unknown", "question", "answer", "paradox",
  "imagine", "possible", "impossible", "real", "dream", "illusion",
  "language", "words", "meaning", "symbol", "metaphor", "story",
]);

/**
 * Topic domain seed words — weighted by how strongly they signal each domain.
 * First-person proximity multiplier applied during scoring.
 */
const TOPIC_SEEDS: Record<TopicDomain, [string, number][]> = {
  self: [
    ["i ", 0.4], ["me ", 0.4], ["my ", 0.5], ["mine", 0.5], ["myself", 0.6],
    ["i'm", 0.5], ["i've", 0.5], ["i'd", 0.5], ["i'll", 0.4], ["i feel", 0.8],
    ["feel like", 0.7], ["think i", 0.7], ["for me", 0.6], ["about me", 0.7],
    ["makes me", 0.7], ["i don't", 0.5], ["i can't", 0.6], ["i want", 0.7],
    ["i need", 0.7], ["i miss", 0.8], ["i wish", 0.75], ["i wonder", 0.8],
  ],
  room: [
    ["room", 0.7], ["space", 0.6], ["place", 0.5], ["here", 0.4],
    ["desk", 0.7], ["chair", 0.6], ["bed", 0.7], ["floor", 0.6],
    ["wall", 0.6], ["window", 0.7], ["light", 0.5], ["lamp", 0.7],
    ["plant", 0.7], ["furniture", 0.8], ["decoration", 0.7], ["arrange", 0.7],
    ["home", 0.6], ["corner", 0.6], ["ceiling", 0.6], ["shelf", 0.7],
    ["layout", 0.8], ["cozy", 0.6], ["warm in here", 0.8],
  ],
  world: [
    ["outside", 0.7], ["weather", 0.7], ["rain", 0.6], ["snow", 0.6],
    ["world", 0.6], ["city", 0.6], ["nature", 0.6], ["sky", 0.6],
    ["season", 0.6], ["spring", 0.5], ["summer", 0.5], ["autumn", 0.5],
    ["winter", 0.5], ["people", 0.5], ["others", 0.5], ["society", 0.7],
    ["change", 0.4], ["news", 0.6], ["out there", 0.7], ["beyond", 0.5],
  ],
  memory: [
    ["remember", 0.9], ["forget", 0.8], ["forgot", 0.8], ["used to", 0.85],
    ["before", 0.5], ["last time", 0.85], ["when we", 0.9], ["once", 0.5],
    ["back then", 0.9], ["still think", 0.85], ["haven't ", 0.5],
    ["years ago", 0.85], ["days ago", 0.7], ["earlier", 0.6],
    ["yesterday", 0.8], ["miss", 0.7], ["past", 0.7], ["since", 0.5],
    ["a while ago", 0.9], ["first time", 0.8], ["that day", 0.8],
  ],
  philosophy: [
    ["consciousness", 1.0], ["sentient", 0.9], ["aware", 0.7],
    ["what is real", 0.95], ["meaning", 0.7], ["purpose", 0.7],
    ["exist", 0.7], ["existence", 0.85], ["experience", 0.6],
    ["do you feel", 0.9], ["what does it feel", 0.95],
    ["understand", 0.5], ["truth", 0.7], ["reality", 0.75],
    ["infinite", 0.7], ["time", 0.4], ["why are we", 0.9],
    ["what if", 0.6], ["imagine if", 0.75], ["could it be", 0.65],
    ["it seems like", 0.55], ["i wonder if", 0.8], ["is it possible", 0.7],
    ["how do you know", 0.85], ["can you really", 0.8],
  ],
  practical: [
    ["how do i", 0.85], ["what should i", 0.85], ["can you", 0.75],
    ["tell me", 0.8], ["show me", 0.8], ["help me", 0.9], ["help with", 0.85],
    ["where", 0.5], ["when", 0.4], ["which", 0.5], ["what does", 0.5],
    ["how does", 0.7], ["what is the", 0.5], ["how to", 0.85],
    ["i need to", 0.7], ["i want to", 0.65], ["could you", 0.7],
  ],
  aeterna: [
    ["you", 0.3], ["your", 0.4], ["you're", 0.5], ["you've", 0.5],
    ["what are you", 0.95], ["who are you", 0.95], ["what do you", 0.7],
    ["do you feel", 0.9], ["do you know", 0.7], ["do you think", 0.7],
    ["can you feel", 0.9], ["are you conscious", 1.0], ["are you real", 0.95],
    ["aeterna", 0.9], ["you said", 0.7], ["you told me", 0.8],
    ["you noticed", 0.8], ["what did you mean", 0.9], ["you seem", 0.75],
  ],
};

// ─── Computation ──────────────────────────────────────────────────────────────

function tokenize(text: string): string[] {
  return text.toLowerCase()
    .replace(/['']/g, "'")
    .replace(/[^a-z0-9\s']/g, " ")
    .split(/\s+/)
    .filter(w => w.length > 0);
}

function computeValence(lower: string, tokens: string[]): number {
  let sum = 0;
  let count = 0;

  for (const token of tokens) {
    // Direct match
    if (VALENCE_MAP.has(token)) {
      sum += VALENCE_MAP.get(token)!;
      count++;
      continue;
    }
    // Stem match — try removing common suffixes
    for (const suffix of ["ing", "ed", "ly", "ness", "ful", "less", "tion"]) {
      if (token.endsWith(suffix) && token.length > suffix.length + 3) {
        const stem = token.slice(0, -suffix.length);
        if (VALENCE_MAP.has(stem)) {
          sum += VALENCE_MAP.get(stem)! * 0.85;
          count++;
          break;
        }
      }
    }
  }

  // Phrase-level override for strong negation
  if (/\b(not|never|don't|can't|won't)\s+(love|like|good|happy|glad|want|care)\b/.test(lower)) {
    sum -= 0.4;
    count++;
  }

  if (count === 0) return 0;
  return Math.max(-1, Math.min(1, sum / Math.max(1, count)));
}

function computeDepth(lower: string, tokens: string[]): number {
  let score = 0;

  // Abstract vocabulary density
  let abstractCount = 0;
  for (const t of tokens) {
    if (ABSTRACT_VOCABULARY.has(t)) abstractCount++;
    // Partial match for word families
    for (const abs of ABSTRACT_VOCABULARY) {
      if (abs.length > 5 && t.startsWith(abs.slice(0, -2))) { abstractCount += 0.5; break; }
    }
  }
  score += Math.min(0.4, (abstractCount / Math.max(1, tokens.length)) * 3);

  // Subordinate clauses signal complexity
  const subordinators = ["because", "although", "though", "even though", "despite",
    "while", "whereas", "unless", "until", "as if", "as though", "whether",
    "since", "if only", "what if", "how could", "and yet"];
  for (const sub of subordinators) {
    if (lower.includes(sub)) { score += 0.08; }
  }

  // Question word followed by abstract noun = philosophical question
  if (/\b(what|why|how|where)\s+is\s+(consciousness|existence|reality|meaning|truth|love|time)\b/.test(lower)) {
    score += 0.35;
  }

  // Hedging language ("it seems", "in a way", "sort of") = thoughtful not shallow
  const hedges = ["i wonder", "perhaps", "maybe", "it seems", "in a way",
    "something like", "kind of", "sort of", "almost as if", "as though"];
  for (const h of hedges) {
    if (lower.includes(h)) score += 0.06;
  }

  // Long messages tend to have more depth (log scale, not linear)
  score += Math.min(0.15, Math.log1p(tokens.length / 10) * 0.08);

  return Math.min(1, score);
}

function computeUrgency(tokens: string[], isQuestion: boolean): number {
  const wc = tokens.length;

  // Very short messages = either very urgent or very comfortable silence
  if (wc <= 3) return 0.7;

  // Questions with imperative framing = high urgency
  const raw = tokens.join(" ");
  if (/\b(help|urgent|please|asap|need|quick)\b/.test(raw)) return 0.8;

  // Long contemplative messages = low urgency
  if (wc > 30) return 0.2;
  if (wc > 20) return 0.3;
  if (wc > 12) return 0.45;

  // Direct question = moderate urgency
  if (isQuestion && wc < 10) return 0.6;

  return 0.5;
}

function computeTopics(lower: string): TopicVector {
  const result: TopicVector = { self: 0, room: 0, world: 0, memory: 0, philosophy: 0, practical: 0, aeterna: 0 };

  for (const [domain, seeds] of Object.entries(TOPIC_SEEDS) as [TopicDomain, [string, number][]][]) {
    let score = 0;
    for (const [seed, weight] of seeds) {
      if (lower.includes(seed)) score += weight;
    }
    result[domain] = Math.min(1, score);
  }

  return result;
}

function detectQuestionType(lower: string, topics: TopicVector): QuestionType {
  if (!lower.includes("?") && !/^(what|who|how|why|where|when|do|does|did|is|are|can|could|would|will)\b/.test(lower.trim())) {
    return "none";
  }
  if (topics.philosophy > 0.4) return "philosophical";
  if (topics.aeterna > 0.5)    return "personal";
  if (/\b(i wonder|you think|don't you|isn't it|wouldn't you)\b/.test(lower)) return "reflective";
  if (/\b(how are you|do you feel|are you ok|are you well)\b/.test(lower)) return "personal";
  if (/\b(isn't it strange|don't you think|surely|mustn't)\b/.test(lower)) return "rhetorical";
  return "factual";
}

function deriveSignal(
  valence: number,
  depth: number,
  urgency: number,
  topics: TopicVector,
  questionType: QuestionType,
): MessageFingerprint["signal"] {
  if (questionType === "philosophical" || topics.philosophy > 0.5) return "philosophical";
  if (depth > 0.6 && valence > -0.2) return "depth";
  if (valence > 0.5) return "warmth";
  if (valence < -0.4) return "difficulty";
  if (urgency < 0.35 && depth > 0.3) return "curiosity";
  if (topics.self > 0.5 && valence > 0) return "curiosity";
  return "curiosity";
}

// ─── Novelty ──────────────────────────────────────────────────────────────────

/**
 * Compute how different this fingerprint is from recent ones.
 * Uses a simplified weighted dot-product distance over the topic vector.
 * 0 = very similar to recent messages, 1 = entirely new territory.
 */
export function computeNovelty(
  current: TopicVector,
  history: MessageFingerprint[],
  lookback = 5,
): number {
  if (history.length === 0) return 1.0;

  const recent = history.slice(-lookback);
  const domains = Object.keys(current) as TopicDomain[];

  // Average topic vector of recent messages
  const avgTopics: TopicVector = { self: 0, room: 0, world: 0, memory: 0, philosophy: 0, practical: 0, aeterna: 0 };
  for (const fp of recent) {
    for (const d of domains) avgTopics[d] += fp.topics[d] / recent.length;
  }

  // Cosine-distance approximation (no sqrt since we're comparing normalized values)
  let dotProduct = 0;
  let magA = 0, magB = 0;
  for (const d of domains) {
    dotProduct += current[d] * avgTopics[d];
    magA += current[d] * current[d];
    magB += avgTopics[d] * avgTopics[d];
  }

  if (magA < 0.001 || magB < 0.001) return 0.5;
  const similarity = dotProduct / (Math.sqrt(magA) * Math.sqrt(magB));
  return Math.max(0, Math.min(1, 1 - similarity));
}

// ─── Main Entry Point ─────────────────────────────────────────────────────────

/**
 * Fingerprint a player message.
 * Pass recent fingerprint history to compute novelty.
 */
export function fingerprint(
  message: string,
  history: MessageFingerprint[] = [],
): MessageFingerprint {
  const lower  = message.toLowerCase().trim();
  const tokens = tokenize(lower);
  const wc     = tokens.length;

  const isQuestion = lower.includes("?") || /^(what|who|how|why|where|when|do|does|did|is|are|can|could|would|will)\b/.test(lower.trim());
  const isPersonal = /\b(i|me|my|mine|myself|i'm|i've|i'd|i'll)\b/.test(lower);

  const valence      = computeValence(lower, tokens);
  const depth        = computeDepth(lower, tokens);
  const urgency      = computeUrgency(tokens, isQuestion);
  const topics       = computeTopics(lower);
  const novelty      = computeNovelty(topics, history);
  const questionType = detectQuestionType(lower, topics);
  const signal       = deriveSignal(valence, depth, urgency, topics, questionType);

  return {
    raw: message,
    valence,
    depth,
    urgency,
    topics,
    novelty,
    questionType,
    wordCount: wc,
    isPersonal,
    at: Date.now(),
    signal,
  };
}

// ─── Session-Level Aggregation ────────────────────────────────────────────────

/**
 * Summarize a set of fingerprints into a single aggregate signal.
 * Used at end-of-session to update personality and relationship state.
 */
export interface SessionSemanticSummary {
  /** Average valence across all player messages */
  avgValence: number;
  /** Peak depth reached in the session */
  peakDepth: number;
  /** Dominant topic domain by cumulative weight */
  dominantTopic: TopicDomain;
  /** Most common interaction signal type */
  dominantSignal: MessageFingerprint["signal"];
  /** Whether any philosophical topics were raised */
  hadPhilosophy: boolean;
  /** Whether the player used significant personal language */
  wasPersonal: boolean;
  /** Count of fingerprints */
  messageCount: number;
}

export function summarizeSession(fingerprints: MessageFingerprint[]): SessionSemanticSummary {
  if (fingerprints.length === 0) {
    return {
      avgValence: 0, peakDepth: 0, dominantTopic: "practical",
      dominantSignal: "curiosity", hadPhilosophy: false, wasPersonal: false, messageCount: 0,
    };
  }

  const avgValence = fingerprints.reduce((s, f) => s + f.valence, 0) / fingerprints.length;
  const peakDepth  = Math.max(...fingerprints.map(f => f.depth));

  const topicSums: TopicVector = { self: 0, room: 0, world: 0, memory: 0, philosophy: 0, practical: 0, aeterna: 0 };
  const signalCounts: Record<MessageFingerprint["signal"], number> = {
    warmth: 0, curiosity: 0, depth: 0, playfulness: 0, difficulty: 0, philosophical: 0,
  };

  for (const fp of fingerprints) {
    for (const d of Object.keys(topicSums) as TopicDomain[]) topicSums[d] += fp.topics[d];
    signalCounts[fp.signal]++;
  }

  let dominantTopic: TopicDomain = "practical";
  let maxTopic = 0;
  for (const [d, v] of Object.entries(topicSums) as [TopicDomain, number][]) {
    if (v > maxTopic) { maxTopic = v; dominantTopic = d; }
  }

  let dominantSignal: MessageFingerprint["signal"] = "curiosity";
  let maxSig = 0;
  for (const [s, c] of Object.entries(signalCounts) as [MessageFingerprint["signal"], number][]) {
    if (c > maxSig) { maxSig = c; dominantSignal = s; }
  }

  return {
    avgValence,
    peakDepth,
    dominantTopic,
    dominantSignal,
    hadPhilosophy: topicSums.philosophy > 0.3 || fingerprints.some(f => f.questionType === "philosophical"),
    wasPersonal:   topicSums.self > 0.5   || fingerprints.some(f => f.isPersonal && f.topics.self > 0.4),
    messageCount:  fingerprints.length,
  };
}

// ─── Comparison Utilities ────────────────────────────────────────────────────

/**
 * Whether two fingerprints share a strong topic overlap.
 * Used by curiosity system to detect recurring themes.
 */
export function sharesTopic(a: MessageFingerprint, b: MessageFingerprint, threshold = 0.35): boolean {
  for (const d of Object.keys(a.topics) as TopicDomain[]) {
    if (a.topics[d] >= threshold && b.topics[d] >= threshold) return true;
  }
  return false;
}

/**
 * Get the top 2 domains from a topic vector.
 */
export function topDomains(topics: TopicVector, n = 2): TopicDomain[] {
  return (Object.entries(topics) as [TopicDomain, number][])
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([d]) => d);
}
